/**
 * Creep pet stats — persisted in localStorage. Only affects animations
 * and greetings on the home screen; does NOT touch any app data or APIs.
 */
const KEY = "creep.pet.v1";
const DAY_KEY = "creep.pet.lastday.v1";

const DEFAULT = {
  happiness: 78,
  energy: 68,
  mood: 72,
  curiosity: 85,
  accessories: [],       // list of unlocked cosmetics
  pets: 0,               // lifetime pet-count
  meals: 0,
  snowballs: 0,
  lastVisit: null,
  streak: 1,
};

const clamp = (v) => Math.max(0, Math.min(100, Math.round(v)));

export const loadPet = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    return { ...DEFAULT, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT };
  }
};

export const savePet = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent("creep:pet", { detail: state }));
  } catch {
    /* ignore */
  }
  return state;
};

/** Slight decay over time so the pet feels alive. */
export const applyIdleDecay = (state) => {
  const now = Date.now();
  const last = state.lastVisit || now;
  const hoursAway = Math.max(0, (now - last) / (1000 * 60 * 60));
  if (hoursAway < 0.05) return state;
  const decay = Math.min(30, Math.floor(hoursAway * 2));
  return {
    ...state,
    happiness: clamp(state.happiness - decay),
    energy: clamp(state.energy - decay * 0.6),
    mood: clamp(state.mood - decay * 0.8),
    lastVisit: now,
  };
};

/** Detect daily visit and increment streak. Returns {isNewDay, state}. */
export const rollDailyGreeting = (state) => {
  const today = new Date().toISOString().slice(0, 10);
  let last = null;
  try {
    last = localStorage.getItem(DAY_KEY);
  } catch {
    /* ignore */
  }
  if (last === today) return { isNewDay: false, state };
  const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const streak = last === yest ? (state.streak || 1) + 1 : 1;
  const next = {
    ...state,
    streak,
    happiness: clamp(state.happiness + 12),
    mood: clamp(state.mood + 8),
    accessories: unlockForStreak(state.accessories, streak),
  };
  try {
    localStorage.setItem(DAY_KEY, today);
  } catch {
    /* ignore */
  }
  return { isNewDay: true, state: next };
};

const ACCESSORIES_UNLOCK = {
  1: "scarf",
  3: "goggles",
  7: "snowboard",
  14: "cloak",
};

const unlockForStreak = (current, streak) => {
  const next = new Set(current || []);
  Object.entries(ACCESSORIES_UNLOCK).forEach(([d, acc]) => {
    if (streak >= parseInt(d, 10)) next.add(acc);
  });
  return Array.from(next);
};

export const petCreep = (state) => ({
  ...state,
  happiness: clamp(state.happiness + 5),
  mood: clamp(state.mood + 4),
  curiosity: clamp(state.curiosity + 2),
  pets: (state.pets || 0) + 1,
});

export const feedCreep = (state) => ({
  ...state,
  energy: clamp(state.energy + 12),
  happiness: clamp(state.happiness + 6),
  mood: clamp(state.mood + 3),
  meals: (state.meals || 0) + 1,
});

export const throwSnowball = (state) => ({
  ...state,
  happiness: clamp(state.happiness + 8),
  energy: clamp(state.energy - 4),
  curiosity: clamp(state.curiosity + 8),
  snowballs: (state.snowballs || 0) + 1,
});

export const sleepCreep = (state) => ({
  ...state,
  energy: clamp(state.energy + 30),
  mood: clamp(state.mood + 5),
});

/** Derived: overall mood label used to pick the character pose. */
export const derivePose = (state) => {
  const avg = (state.happiness + state.mood + state.energy) / 3;
  if (avg >= 78) return "happy";
  if (avg >= 55) return "idle";
  if (avg >= 32) return "bored";
  return "sleep";
};

/** Build a weather-aware greeting sentence in Spanish. */
export const buildGreeting = (dashboardData, streak) => {
  const stations = dashboardData?.stations || [];
  if (stations.length === 0) return "Mrr… listo para deslizarnos?";
  // Only consider stations with available data
  const available = stations.filter((s) => s?.available !== false && s?.ski);
  if (available.length === 0) {
    return "Aún no tengo datos de ninguna estación. Vuelve en un rato.";
  }
  // Pick the station with the highest ski score today
  const best = [...available].sort((a, b) => (b.ski.score || 0) - (a.ski.score || 0))[0];
  const score = best.ski.score;
  const name = best.station.name;
  const temp = best.weather?.temperature != null ? Math.round(best.weather.temperature) : null;
  if (best.ski.skiable && score >= 75) return `${name} está en condiciones excelentes hoy.`;
  if (best.ski.skiable && score >= 55) return `${name} pinta muy bien. ¿Vamos?`;
  if (!best.ski.skiable && temp != null && temp > 8) return `Hoy no se puede esquiar. ${temp}° en ${name}.`;
  if (streak > 1) return `Hola de nuevo. Racha de ${streak} días — sigue así.`;
  return `${name}: ${best.ski.label}.`;
};

export const ACCESSORY_LABELS = {
  scarf: "Bufanda",
  goggles: "Gafas de nieve",
  snowboard: "Snowboard",
  cloak: "Manto Susanoo",
};

export default {
  loadPet,
  savePet,
  applyIdleDecay,
  rollDailyGreeting,
  petCreep,
  feedCreep,
  throwSnowball,
  sleepCreep,
  derivePose,
  buildGreeting,
};
